import { baseApiAxios } from './api';
import { getCurrentUserName } from './auth';
import { addCopyButton, displayErrorPopup, scrollToPosition, checkValueAgainstList } from './main';
import { addSpinner, addTableSpinner, removeSpinners } from './sidebar';
import { getRowData, getDisableTarget, validateModalForm, submitModalOrder, FormField } from './modalUtils';

import { initDataTable } from './datatable';
import { Modal } from 'bootstrap';
import UserDropdown from '../../jsx/components/forms/UserDropdown';

function M2MClientReadonlyFields({ clientId, mappedUsername }) {
  // NOSONAR - custom JSX runtime, no propTypes
  return (
    <>
      <FormField id="m2m-client-id" label="Client ID" required disabled value={clientId} />
      <FormField id="m2m-client-mapped-username" label="Mapped Username" required disabled value={mappedUsername} />
    </>
  );
}

export async function loadM2MClientPortalAPIAccessTable(tableId, listAll = false) {
  $(() => addSpinner());
  $(() => addTableSpinner());

  try {
    const { items } = await baseApiAxios.getM2MClientPortalAPIAccessInfo(listAll);
    // No need to fetch next token. Because we are not passing a limit and the Backend API also does not limit the results.
    // This is because unlike the VPC and Accounts list page, this page/table will very unlikely have thousands of items, so fetching will be fast.
    const table = $('#' + tableId).DataTable();
    table.clear();
    table.rows.add(items);
    table.columns.adjust().draw();

    $(window).trigger('resize');
    scrollToPosition();
  } catch (e) {
    displayErrorPopup(e);
  } finally {
    removeSpinners();
  }
}

export function initM2MClientAccessTable(tableId) {
  let userPortalPermissions = localStorage.permissions ? localStorage.permissions : false;
  const hasPermissionToViewAll = checkValueAgainstList('manage_m2m_clients_portal_api_access', userPortalPermissions);
  // Do not show the "List All M2M Access Info" checkbox if the user does not have permission to view all.
  // Only portal admins can view all.
  if (hasPermissionToViewAll) {
    $('#list-all-m2m-access-info-div').removeClass('d-none');
  }

  $('#list-all-m2m-access-info-switch')
    .attr('checked', false)
    .on('change', function () {
      $('#' + tableId)
        .DataTable()
        .clear();
      loadM2MClientPortalAPIAccessTable(tableId, this.checked);
    });

  initDataTable(
    tableId,
    [
      {
        extend: 'reloadTable',
        text: 'Reload List',
        ajaxReload: false,
        methodReload: tableId => {
          loadM2MClientPortalAPIAccessTable(tableId, $('#list-all-m2m-access-info-switch').is(':checked'));
        },
        titleAttr: 'Reload List',
      },
      {
        text: 'Request Access',
        action: function () {
          openRequestAccessModal(tableId);
        },
        titleAttr: 'Request Access',
      },
    ],
    [
      {
        visible: true,
        defaultContent: '-',
        orderable: true,
        searchable: true,
        type: 'string',
        data: 'client_id',
        name: 'id_col',
        title: 'M2M Client ID',
        createdCell: function (td) {
          addCopyButton(td);
        },
      },
      {
        visible: true,
        defaultContent: false,
        orderable: true,
        searchable: true,
        type: 'string',
        data: 'permission_mapped_to',
        name: 'mapped_username_col',
        title: 'Mapped Username',
        createdCell: function (td) {
          addCopyButton(td);
        },
      },
      {
        visible: true,
        defaultContent: '-',
        orderable: true,
        searchable: true,
        type: 'string',
        data: 'description',
        name: 'description_col',
        title: 'Description',
        createdCell: function (td) {
          addCopyButton(td);
        },
      },
      {
        visible: false,
        defaultContent: '-',
        orderable: true,
        searchable: true,
        type: 'string',
        data: 'last_accessed_path',
        name: 'last_accessed_path_col',
        title: 'Last Accessed Path',
        createdCell: addCopyButton,
      },
      {
        visible: true,
        defaultContent: '-',
        orderable: true,
        searchable: true,
        type: 'string',
        data: 'created_date',
        name: 'created_date_col',
        title: 'Created Date',
        createdCell: addCopyButton,
      },
      {
        visible: true,
        defaultContent: '',
        orderable: true,
        searchable: true,
        type: 'string',
        data: 'responsibles',
        name: 'responsibles_col',
        title: 'Responsibles',
        createdCell: function (td, cellData) {
          addCopyButton(td);
          if (Array.isArray(cellData) && cellData.length > 0) {
            const currentUser = getCurrentUserName().toLowerCase();
            const userIDs = cellData.map((data, index) => (
              <span className={currentUser == data.toLowerCase() ? 'current-user-responsible' : ''}>
                {data}
                {index < cellData.length - 1 ? ', ' : ''}
              </span>
            ));
            $(td).html(userIDs).css('min-width', '400px').css('white-space', 'normal').css('word-break', 'break-word');
          }
        },
      },
      {
        visible: true,
        defaultContent: '',
        orderable: false,
        searchable: true,
        data: null,
        name: 'actions_col',
        title: 'Actions',
        width: '50px',
        createdCell: function (td, _cellData, rowData, row, _col) {
          const buttons = $('<div class="table-action-button-group">').appendTo(td);
          // DELETE BUTTON
          buttons.append(
            $(
              '<button class="btn btn-custom btn-xs revoke-m2m-api-access" data-bs-toggle="tooltip" title="Revoke M2M Access"/>',
            )
              .on('click', evt => openRevokeAccessModal(tableId, rowData, evt))
              .append('<i class="fas fa-trash-alt">'),
          );
          // UPDATE BUTTON
          buttons.append(
            $(
              '<button class="btn btn-custom btn-xs update-m2m-api-access" data-bs-toggle="tooltip" title="Update M2M Access Info"/>',
            )
              .on('click', evt => openUpdateAccessModal(tableId, rowData, evt))
              .append('<i class="fas fa-pencil-alt icon-color">'),
          );
        },
      },
    ],
    function (row, data) {
      if (data.is_default) {
        $(row).addClass('row-default');
      }
    },
    {
      order: [[1, 'asc']],
    },
  );

  loadM2MClientPortalAPIAccessTable(tableId, $('#list-all-m2m-access-info-switch').is(':checked'));
}

export function openUpdateAccessModal(tableId, rowData, clickEvent) {
  const updateButton = getDisableTarget(clickEvent, 'update-m2m-api-access');
  const existingM2MClientAccessInfo = getRowData(tableId, rowData);

  const modal = $('#sharedModal');
  const ModalTitle = () => <strong>Update M2M Client Access Info</strong>;
  $('#sharedModalTitle').html(<ModalTitle />);

  const ConfirmButton = () => {
    return (
      <button type="submit" class="btn btn-success" id="submit-update-m2m-api-access-button">
        Submit
      </button>
    );
  };

  const Content = () => {
    return (
      <>
        <form id="update-m2m-api-access-form" name="update-m2m-api-access-form" class="needs-validation" method="POST">
          <M2MClientReadonlyFields
            clientId={existingM2MClientAccessInfo.client_id}
            mappedUsername={existingM2MClientAccessInfo.permission_mapped_to}
          />
          <div class="form-group row">
            <UserDropdown
              id="m2m-client-responsibles"
              label="Responsibles"
              deselect
              multiple
              disableUrlParams={true}
              selectedValues={existingM2MClientAccessInfo.responsibles}
              userType="human"
              maxSelectable="20"
            />
          </div>
          <FormField
            id="client-description"
            label="Description"
            placeholder="A brief description of what this client will be used for."
            title="client-description"
            maxlength="100"
            value={existingM2MClientAccessInfo.description || ''}
          />
        </form>
      </>
    );
  };

  $('#sharedModalFooter').append(<ConfirmButton />);
  $('#sharedModalContent').html(<Content />);

  const validateAndConfirm = async () => {
    if (!validateModalForm()) return;

    const formData = new FormData(document.getElementById('update-m2m-api-access-form'));
    const orderPayload = {
      action: 'update-m2m-api-access',
      client_id: $('#m2m-client-id').val(),
      responsibles: formData.getAll('m2m-client-responsibles'),
      client_description: formData.get('client-description'),
      description: `Update M2M Client Access Info for M2M client ${$('#m2m-client-id').val()}`,
    };
    await submitModalOrder(
      modal,
      'submit-update-m2m-api-access-button',
      orderPayload,
      `Your order to update the access info for an M2M Client has been submitted (order ID: {order_id}). Please visit the orders details page to track the progress of your order.`,
    );
  };
  $('#submit-update-m2m-api-access-button').on('click', validateAndConfirm);
  modal.on('hide.bs.modal', () => {
    updateButton.disabled = false;
  });
  Modal.getInstance(modal).show();
}

export function openRevokeAccessModal(tableId, rowData, clickEvent) {
  const revokeButton = getDisableTarget(clickEvent, 'revoke-m2m-api-access');
  const existingM2MClientAccessInfo = getRowData(tableId, rowData);

  const modal = $('#sharedModal');
  const ModalTitle = () => <strong>Revoke M2M Client Access</strong>;
  $('#sharedModalTitle').html(<ModalTitle />);

  const ConfirmButton = () => {
    return (
      <button type="submit" class="btn btn-warning" id="submit-revoke-m2m-api-access-button">
        Submit Request to Revoke Access
      </button>
    );
  };

  const Content = () => {
    const subtitle = 'Are you sure ?';
    const warningText =
      'Once you revoke access, the M2M Client will no longer be able to access the Portal API. To request a new access, please submit a new order.';
    return (
      <>
        <h3 align="center">{subtitle}</h3>
        <div class="alert alert-warning px-3 py-2 mb-3 bold-text">{warningText}</div>
        <form id="revoke-m2m-api-access-form" name="revoke-m2m-api-access-form" class="needs-validation" method="POST">
          <M2MClientReadonlyFields
            clientId={existingM2MClientAccessInfo.client_id}
            mappedUsername={existingM2MClientAccessInfo.permission_mapped_to}
          />
        </form>
      </>
    );
  };

  $('#sharedModalFooter').append(<ConfirmButton />);
  $('#sharedModalContent').html(<Content />);

  const validateAndConfirm = async () => {
    if (!validateModalForm()) return;

    const orderPayload = {
      action: 'revoke-m2m-api-access',
      description: `Revoke M2M Client Access Info for M2M client ${$('#m2m-client-id').val()}`,
      client_id: $('#m2m-client-id').val(),
    };
    await submitModalOrder(
      modal,
      'submit-revoke-m2m-api-access-button',
      orderPayload,
      `Your order to revoke the access info for an M2M Client has been submitted (order ID: {order_id}). Please visit the orders details page to track the progress of your order.`,
    );
  };
  $('#submit-revoke-m2m-api-access-button').on('click', validateAndConfirm);
  modal.on('hide.bs.modal', () => {
    revokeButton.disabled = false;
  });
  Modal.getInstance(modal).show();
}

export function openRequestAccessModal(tableId) {
  const modal = $('#sharedModal');
  const ModalTitle = () => <strong>Request Portal API Access for your M2M Client</strong>;
  $('#sharedModalTitle').html(<ModalTitle />);

  const ConfirmButton = () => {
    return (
      <button type="submit" class="btn btn-success" id="submit-grant-m2m-api-access-button">
        Submit
      </button>
    );
  };

  const Content = () => {
    return (
      <>
        <form id="grant-m2m-api-access-form" name="grant-m2m-api-access-form" class="needs-validation" method="POST">
          <FormField
            id="m2m-client-id"
            label="Client ID"
            placeholder="The M2M Client ID that you want to request Portal API access for."
            title="m2m-client-id"
            maxlength="100"
            pattern="^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
            required
          />
          <div class="form-group row">
            <UserDropdown
              id="m2m-client-mapped-username"
              label="Mapped Username"
              disableUrlParams={true}
              userType="service"
            />
          </div>
          <div class="form-group row">
            <UserDropdown
              id="m2m-client-responsibles"
              label="Responsibles"
              deselect
              multiple
              disableUrlParams={true}
              userType="human"
              maxSelectable="20"
            />
          </div>
          <FormField
            id="client-description"
            label="Description"
            placeholder="A brief description of what this client will be used for."
            title="client-description"
            maxlength="100"
          />
        </form>
      </>
    );
  };

  $('#sharedModalFooter').append(<ConfirmButton />);
  $('#sharedModalContent').html(<Content />);

  const validateAndConfirm = async () => {
    if (!validateModalForm()) return;

    const formData = new FormData(document.getElementById('grant-m2m-api-access-form'));
    const orderPayload = {
      action: 'grant-m2m-api-access',
      client_id: formData.get('m2m-client-id'),
      mapped_username: formData.get('m2m-client-mapped-username'),
      responsibles: formData.getAll('m2m-client-responsibles'),
      client_description: formData.get('client-description'),
      description: `Requesting Portal API Access for M2M Client ${formData.get('m2m-client-id')}`,
    };
    await submitModalOrder(
      modal,
      'submit-grant-m2m-api-access-button',
      orderPayload,
      `Your order to request access for an M2M Client has been submitted (order ID: {order_id}). It will now be evaluated for approval. Please visit the orders details page to track the progress of your order.`,
    );
  };
  $('#submit-grant-m2m-api-access-button').on('click', validateAndConfirm);
  Modal.getInstance(modal).show();
}
